import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Plus } from 'lucide-react';
import { SystemRole, formatTimeDisplay, hasMinimumRole, type Timeline } from '@peles/shared';
import { useQuery } from '@tanstack/react-query';
import { exportTimeline } from '../features/timelinePortable';
import { ImportTimelineButton } from '../components/event-ops/ImportTimelineButton';
import { useCreateTimeline, useDeleteTimeline, useTimelines } from '../hooks/useEventOps';
import { useEvent, useEventTags } from '../hooks/useEvents';
import { getAppLogoUrl } from '../api/events';
import { TimelinePrintView } from '../components/timeline/TimelinePrintView';
import { TimelineCard } from '../components/timeline/TimelineCard';
import { useDialog } from '../context/DialogContext';
import { useInEventWorkspace } from '../components/events/workspace/EventWorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useThreadCounts } from '../hooks/useThreadCounts';

// Re-exported for existing consumers/tests; the implementation lives in @peles/shared.
export { formatTimeDisplay };

function formatEventDateRange(startAt: Date | string, endAt: Date | string | null): string {
  const start = new Date(startAt);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  if (!endAt) return start.toLocaleDateString('en-US', opts);
  const end = new Date(endAt);
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  if (sameMonth) {
    if (start.getDate() === end.getDate()) return start.toLocaleDateString('en-US', opts);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.getDate()}, ${start.getFullYear()}`;
  }
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${sameYear ? `, ${start.getFullYear()}` : ''}`;
}

/** Where a timeline is edited: on its event, as a template, or on its own. */
export function timelineEditorPath(timeline: Timeline): string {
  if (timeline.eventId) return `/events/${timeline.eventId}/timeline/${timeline.id}`;
  if (timeline.isTemplate) return `/templates/timeline/${timeline.id}`;
  return `/timelines/${timeline.id}`;
}

/**
 * An event's timelines (its Timeline tab), or every timeline (`/timelines`):
 * a card each, opening the editor. A new timeline opens straight away.
 */
export function TimelinePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const inWorkspace = useInEventWorkspace();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { data: timelines = [], isLoading, isError, refetch } = useTimelines(eventId);
  const { data: event } = useEvent(eventId ?? '');
  const { data: eventTags = [] } = useEventTags(eventId);
  const { data: appLogoUrl } = useQuery({
    queryKey: ['app-logo-url'],
    queryFn: getAppLogoUrl,
    staleTime: 60_000,
  });
  const createTimeline = useCreateTimeline();
  const deleteTimeline = useDeleteTimeline();
  const [printTimeline, setPrintTimeline] = useState<Timeline | null>(null);
  const isEventScoped = !!eventId;
  const { user } = useAuth();
  /*
   * The conversation icon is the office's (COMMUNICATIONS-DESIGN H3/H5): a
   * client reaches this page too, and the server lets anybody below PLANNER
   * attach a conversation to an event only — an icon offering one on a
   * timeline would be a button that refuses. Templates are not conversation
   * records. Nobody shown an icon, nothing counted.
   */
  const office = user != null && hasMinimumRole(user.systemRole, SystemRole.PLANNER);
  const { countFor, refetch: refetchThreadCounts } = useThreadCounts(
    'Timeline',
    office ? timelines.filter((t) => !t.isTemplate).map((t) => t.id) : [],
  );
  const onThreadActivity = () => void refetchThreadCounts();

  const handleCreate = () => {
    const title = isEventScoped && event?.name ? `${event.name} Timeline` : 'New Timeline';
    createTimeline.mutate(
      { eventId, title },
      { onSuccess: (created) => navigate(timelineEditorPath(created)) },
    );
  };

  const handleDelete = async (timeline: Timeline) => {
    const ok = await confirm({
      title: 'Delete timeline',
      message: `Delete “${timeline.title || 'Untitled timeline'}” and everything on it?`,
      confirmLabel: 'Delete',
    });
    if (ok) deleteTimeline.mutate(timeline.id);
  };

  const newButton = (
    <button
      type="button"
      onClick={handleCreate}
      disabled={createTimeline.isPending}
      className="flex items-center gap-2 min-h-11 sm:min-h-0 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
      data-testid="new-timeline-button"
    >
      <Plus size={16} aria-hidden />
      {createTimeline.isPending ? 'Creating…' : 'New timeline'}
    </button>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          {isEventScoped && !inWorkspace && (
            <button
              type="button"
              onClick={() => navigate(`/events/${eventId}`)}
              className="flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 sm:p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Back to event"
              data-testid="back-to-event-button"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEventScoped ? 'Event Timeline' : 'All Timelines'}
            </h1>
            {isEventScoped && event?.startAt && (
              <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                <Calendar size={14} aria-hidden />
                <span data-testid="event-date-header">
                  {formatEventDateRange(event.startAt, event.endAt ?? null)}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ImportTimelineButton eventId={eventId} />
          {newButton}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3" aria-busy="true" aria-label="Loading timelines">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-28 rounded-xl border border-gray-200 bg-white animate-pulse"
            />
          ))}
        </div>
      ) : isError ? (
        <div className="text-center py-10 bg-white rounded-xl border border-gray-200" role="alert">
          <p className="text-sm text-gray-700">The timelines could not be loaded.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-3 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      ) : timelines.length === 0 ? (
        <div className="text-center py-12 px-4 bg-white rounded-xl border border-dashed border-gray-300">
          <Clock className="mx-auto h-12 w-12 text-gray-300" aria-hidden />
          <p className="mt-3 font-medium text-gray-800">No timelines yet</p>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
            {isEventScoped
              ? "Build the day's run of show — what happens, when and where — and print it for the team."
              : 'Timelines made on events show here.'}
          </p>
          <div className="mt-4 flex justify-center">{newButton}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {timelines.map((timeline) => (
            <TimelineCard
              key={timeline.id}
              timeline={timeline}
              href={timelineEditorPath(timeline)}
              onPrint={eventId ? setPrintTimeline : undefined}
              onExport={exportTimeline}
              onDelete={(t) => void handleDelete(t)}
              conversation={
                office && !timeline.isTemplate
                  ? { count: countFor(timeline.id), onActivity: onThreadActivity }
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {printTimeline && (
        <TimelinePrintView
          timeline={printTimeline}
          eventName={event?.name ?? 'Event'}
          eventTags={eventTags}
          appLogoUrl={appLogoUrl ?? null}
          eventLogoUrl={event?.logoUrl ?? null}
          onClose={() => setPrintTimeline(null)}
        />
      )}
    </div>
  );
}
